<template>
  <div class="top-card metric-card" :class="{ 'metric-card--with-image': !!imageUrl }">
    <div v-if="loading" class="card-loading" aria-live="polite">
      <div class="spinner small" aria-hidden="true"></div>
      <span class="card-loading-text">{{ $t('common.updating') }}</span>
    </div>
    <template v-else>
      <div class="card-icon" aria-hidden="true">{{ icon }}</div>
      <div class="card-label">{{ label }}</div>
      <div v-if="imageUrl" class="metric-with-image">
        <img
          :src="imageUrl"
          :alt="value"
          class="metric-thumb"
          loading="lazy"
          @error="onImageError"
        />
        <div class="metric-text">
          <div class="card-value">{{ value }}</div>
          <div v-if="sub" class="card-sub">{{ sub }}</div>
        </div>
      </div>
      <template v-else>
        <div class="card-value">{{ value }}</div>
        <div v-if="sub" class="card-sub">{{ sub }}</div>
      </template>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SummaryMetricCard',
  props: {
    icon: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    sub: {
      type: String,
      default: ''
    },
    imageUrl: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onImageError(e) {
      const el = e?.target
      if (el) el.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.top-card {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  min-height: 96px;
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.2s ease;
}

.top-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary), rgba(102, 192, 244, 0.25));
  border-radius: 8px 8px 0 0;
}

.top-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  border-color: rgba(42, 71, 94, 0.9);
}

.metric-card {
  overflow: hidden;
}

.metric-with-image {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.metric-thumb {
  width: 80px;
  height: 37px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.metric-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.metric-text .card-value {
  font-size: 1.05rem;
  line-height: 1.25;
  /* Two-line clamp instead of nowrap+ellipsis so titles like
     "Tales of Maj'Eyal" or "Counter-Strike 2" stay readable on mobile
     instead of collapsing to "Tel..." or "Cou...". */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.metric-text .card-sub {
  margin-top: 2px;
}

.card-icon {
  font-size: 1.25rem;
  line-height: 1;
  margin-bottom: 8px;
  color: var(--primary);
}

.card-label {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.card-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text);
}

.card-sub {
  font-size: 0.85rem;
  color: var(--muted);
  margin-top: 4px;
}

.card-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: rgba(8, 12, 20, 0.75);
  border-radius: inherit;
  color: var(--muted);
  text-align: center;
}

.card-loading .spinner {
  margin: 0;
}

.card-loading-text {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner.small {
  width: 24px;
  height: 24px;
  border-width: 3px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
